import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { stripHtmlFromLabel } from "../utils/textFormatting";

interface ResourceData {
  id?: string;
  name: string;
  role: string;
  department: string;
  email: string;
  phone: string;
  availability: number; // Percentage 0-100
  skills: string;
  notes: string;
}

interface ResourceDialogProps {
  visible: boolean;
  resourceData?: ResourceData;
  setShowResourceDialog: React.Dispatch<React.SetStateAction<boolean>>;
  handleSaveResource: (resourceData: ResourceData) => void;
  handleDeleteResource?: () => void;
}

const ResourceDialog: React.FC<ResourceDialogProps> = ({
  visible,
  resourceData,
  setShowResourceDialog,
  handleSaveResource,
  handleDeleteResource,
}) => {
  const [name, setName] = useState<string>(resourceData?.name || "");
  const [role, setRole] = useState<string>(resourceData?.role || "");
  const [department, setDepartment] = useState<string>(
    resourceData?.department || ""
  );
  const [email, setEmail] = useState<string>(resourceData?.email || "");
  const [phone, setPhone] = useState<string>(resourceData?.phone || "");
  const [availability, setAvailability] = useState<number>(
    resourceData?.availability || 100
  );
  const [skills, setSkills] = useState<string>(resourceData?.skills || "");
  const [notes, setNotes] = useState<string>(resourceData?.notes || "");

  useEffect(() => {
    setName(resourceData?.name || "");
    setRole(resourceData?.role || "");
    setDepartment(resourceData?.department || "");
    setEmail(resourceData?.email || "");
    setPhone(resourceData?.phone || "");
    setAvailability(resourceData?.availability || 100);
    setSkills(resourceData?.skills || "");
    setNotes(resourceData?.notes || "");
  }, [resourceData, visible]);

  const roleOptions = [
    { label: "Developer", value: "Developer" },
    { label: "Designer", value: "Designer" },
    { label: "Project Manager", value: "Project Manager" },
    { label: "Business Analyst", value: "Business Analyst" },
    { label: "QA Engineer", value: "QA Engineer" },
    { label: "DevOps Engineer", value: "DevOps Engineer" },
    { label: "Product Owner", value: "Product Owner" },
    { label: "Scrum Master", value: "Scrum Master" },
    { label: "Architect", value: "Architect" },
    { label: "Consultant", value: "Consultant" },
    { label: "Other", value: "Other" },
  ];

  const handleSave = () => {
    // Validate required fields
    if (!name || name.trim() === "") {
      alert("Name is required. Please enter a resource name.");
      return;
    }

    handleSaveResource({
      id: resourceData?.id,
      name: name.trim(),
      role,
      department,
      email,
      phone,
      availability,
      skills,
      notes,
    });
    setShowResourceDialog(false);
  };

  return (
    <Dialog
      visible={visible}
      modal
      header="Resource Details"
      className="resource-dialog"
      style={{ width: "700px", maxWidth: "90vw" }}
      onHide={() => setShowResourceDialog(false)}
      footer={
        <div>
          {resourceData?.id && handleDeleteResource && (
            <Button
              label="Delete"
              icon="pi pi-trash"
              className="p-button-danger p-button-text"
              onClick={handleDeleteResource}
            />
          )}
          <Button
            label="Cancel"
            icon="pi pi-times"
            className="p-button-text"
            onClick={() => setShowResourceDialog(false)}
          />
          <Button
            label="Save"
            icon="pi pi-check"
            className="p-button-primary"
            onClick={handleSave}
          />
        </div>
      }
    >
      <div className="resource-dialog-form">
        {/* Name */}
        <div className="resource-dialog-field">
          <label htmlFor="resourceName">
            Name (edit in map) <span className="required">*</span>
          </label>
          <InputText
            id="resourceName"
            value={stripHtmlFromLabel(name)}
            disabled
            style={{ backgroundColor: "#f5f5f5" }}
            placeholder="Enter resource name in map"
          />
        </div>

        {/* Role */}
        <div className="resource-dialog-field">
          <label htmlFor="resourceRole">Role</label>
          <Dropdown
            id="resourceRole"
            value={role}
            options={roleOptions}
            onChange={(e) => setRole(e.value)}
            placeholder="Select role"
            editable
          />
        </div>

        {/* Department */}
        <div className="resource-dialog-field">
          <label htmlFor="resourceDepartment">Department</label>
          <InputText
            id="resourceDepartment"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            placeholder="Enter department"
          />
        </div>

        <div className="resource-dialog-row">
          {/* Email */}
          <div className="resource-dialog-field">
            <label htmlFor="resourceEmail">Email</label>
            <InputText
              id="resourceEmail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              type="email"
            />
          </div>

          {/* Phone */}
          <div className="resource-dialog-field">
            <label htmlFor="resourcePhone">Phone</label>
            <InputText
              id="resourcePhone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 234 567 8900"
            />
          </div>
        </div>

        {/* Availability */}
        <div className="resource-dialog-field">
          <label htmlFor="resourceAvailability">Availability (%)</label>
          <InputNumber
            id="resourceAvailability"
            value={availability}
            onValueChange={(e) => setAvailability(e.value || 100)}
            min={0}
            max={100}
            suffix="%"
          />
        </div>

        {/* Skills */}
        <div className="resource-dialog-field">
          <label htmlFor="resourceSkills">Skills</label>
          <InputText
            id="resourceSkills"
            value={skills}
            onChange={(e) => setSkills(e.target.value)}
            placeholder="e.g., React, TypeScript, Node.js"
          />
        </div>

        {/* Notes */}
        <div className="resource-dialog-field">
          <label htmlFor="resourceNotes">Notes</label>
          <InputTextarea
            id="resourceNotes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional notes about this resource"
            rows={4}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default ResourceDialog;
